import { useCallback, useMemo } from "react";
import useSound from "use-sound";
import type { IRemoteNotification } from "@suprsend/web-sdk";
import {
  getIntentForInboxSound,
  intentKeyToSoundUrlMap,
} from "./suprSendFeedIntent";

/** Default sound when no intent-specific URL is configured. */
export const DEFAULT_INBOX_NOTIFICATION_SOUND = "/sounds/notify.wav";

export type InboxNotificationSoundConfig = {
  notification_intent_sounds_mapping?: unknown;
} | null;

function playSoundUrl(url: string) {
  const audio = new Audio(url);
  audio.play().catch((e) => {
    console.error("Failed to play notification sound URL", e);
  });
}

/**
 * Picks a sound for a new SuprSend feed item: config map by `intent`, else default wav.
 */
export function useInboxNotificationSound(
  mobileConfigs: InboxNotificationSoundConfig
) {
  const [playDefault] = useSound(DEFAULT_INBOX_NOTIFICATION_SOUND, {
    volume: 1,
    preload: true,
  });

  const soundUrlByIntent = useMemo(
    () =>
      intentKeyToSoundUrlMap(
        mobileConfigs?.notification_intent_sounds_mapping
      ),
    [mobileConfigs?.notification_intent_sounds_mapping]
  );

  return useCallback(
    (notification: IRemoteNotification | undefined) => {
      try {
        const intent = getIntentForInboxSound(notification);
        const configuredUrl = intent
          ? String(soundUrlByIntent[intent] ?? "").trim()
          : "";

        if (configuredUrl) {
          playSoundUrl(configuredUrl);
          return;
        }
        playDefault();
      } catch (e) {
        console.error("Inbox notification sound error", e);
        try {
          playDefault();
        } catch (inner) {
          console.error("Default notification sound failed", inner);
        }
      }
    },
    [soundUrlByIntent, playDefault]
  );
}
